import { useState } from "react";
import { useForm } from "react-hook-form";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { toast } from "react-toastify";
import styled from "styled-components";
import { rel8Pink, rel8Purple, rel8White } from "../../globals";
import { mobile } from "../../responsive";
import { createNews, getAllCommittee, getListOfExcos } from "../../utils/api-calls";
import Loading from "../Loading/Loading";
import TextEditor from "../TextEditor";

const BackDrop = styled.div`
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  position: fixed;
  top: 0;
  left: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 10;
`;

const Form = styled.form`
  margin: 20px 0px;
  display: flex;
  flex-direction: column;
`;

const FormDataComp = styled.input`
  padding: 5px 0px;
  background-color: transparent;
  border: none;
  border: 1px solid ${rel8Purple};
  border-radius: 5px;
  padding: 5px;
  color: ${rel8Purple};
  outline: none;
  &::placeholder {
    color: ${rel8Purple};
  }
`;

const FormSelection = styled.select`
  padding: 5px 0px;
  color: ${rel8Purple};
  outline: none;
  border: none;
  border-bottom: 1px solid ${rel8Purple};
  margin: 10px 0px;
  overflow: auto;
`;

const FormOption = styled.option``;

const FormLabel = styled.label`
  display: flex;
  flex-direction: column;
  font-size: 12px;
  margin: 10px 0px;
`;

const ErrorMessage = styled.span`
  color: red;
  font-size: 12px;
`;

const SubCon = styled.div`
  background-color: ${rel8White};
  width: 350px;
  height: 500px;
  border-radius: 10px;
  padding: 20px;
  overflow-y: auto;

  ${mobile({
    width: "250px",
  })}
`;

const SubConHeader = styled.p`
  font-weight: 700;
  text-align: center;
`;

const SubConBtnHold = styled.div`
  display: flex;
  justify-content: space-evenly;
  margin-top: 20px;
`;

const SubConBtn = styled.input`
  padding: 10px 20px;
  border: none;
  border-radius: 5px;
  background-color: ${(props) =>
    props.typex === "filled" ? `${rel8Purple}` : `${rel8Pink}`};
  color: ${(props) =>
    props.typex === "filled" ? `${rel8White}` : `${rel8Purple}`};
  cursor: pointer;

  &:disabled {
    background-color: "#d3d3d3";
  }
`;

const AddNews = ({ close }) => {
  const [content, setContent] = useState('');

  const { register, handleSubmit, watch, formState: { errors } } = useForm({
    defaultValues: {
      news_paragraph: [{ heading: "", paragragh: "" }],
    },
  });

  const {
    isLoading: excoListLoading,
    isFetching: excoListFetching,
    isError: excoListIsError,
    data: excoListData,
  } = useQuery("exco-list", getListOfExcos, {
    refetchOnWindowFocus: false,
    select: (data) => {
      return data.data
        .map((item) => ({ id: item.id, name: item.name }))
        .reverse();
    },
  });

  const {
    isLoading: committeeLoading,
    isError: committeeError,
    isFetching: committeeFetching,
    data: committeeData,
  } = useQuery("all-committees", getAllCommittee, {
    refetchOnWindowFocus: false,
    select: (data) => {
      const result = data.data.map((item) => ({
        id: item.id,
        name: item.name,
      }));
      return result.sort((a, b) => a.id - b.id);
    },
  });

  const queryClient = useQueryClient();

  const { isLoading: createLoading, mutate: createMutate } = useMutation(
    (newsData) => createNews(newsData),
    {
      onMutate: () => {
        toast.info("News Creation in progress", {
          progressClassName: "toastProgress",
          icon: false,
        });
      },
      onSuccess: () => {
        toast.success("News Created", {
          progressClassName: "toastProgress",
          icon: false,
        });
        queryClient.invalidateQueries("all-news");
        close();
      },
      onError: (error) => {
        toast.error("Could not create news");
        if (error?.message?.response?.data?.message?.error) {
          toast.error(`Message: ${error.message.response.data.message.error}`, {
            autoClose: 9000,
          });
        }
      },
    }
  );

  const onSubmit = (data) => {
    const bodyContent = content;
    const image = data.image[0];
    const { news_paragraph, image: img, ...newdata } = data;
    const payload = { image, ...newdata };
    console.log(news_paragraph);
    const formData = new FormData();
    Object.keys(payload)?.forEach((key) => formData.append(key, payload[key]));
    formData.append("news_paragraph", JSON.stringify([{ heading: "Use body key", paragragh: "use body key" }]));
    formData.append('body',bodyContent)

    createMutate(formData);
  };

  return (
    <BackDrop>
      <style>
        {`
            body{
                overflow:hidden;
            }
        `}
      </style>

      {excoListLoading ||
      excoListFetching ||
      committeeLoading ||
      committeeFetching ? (
        <Loading
          loading={
            excoListLoading ||
            excoListFetching ||
            committeeLoading ||
            committeeFetching
          }
        />
      ) : !excoListIsError || !committeeError ? (
        <SubCon style={{ width: "600px" }}>
          <SubConHeader>Add News</SubConHeader>
          <Form onSubmit={handleSubmit(onSubmit)}>
            <FormLabel>
              Name:
              <FormDataComp
                type={"text"}
                {...register("name", { required: true })}
              />
            </FormLabel>

            <FormLabel>
              Image:
              <FormDataComp
                type={"file"}
                accept={"image/*"}
                {...register("image", {
                  required: true,
                  validate: {
                    fileSize: (files) => {
                      const maxSizeMB = 10;
                      if (files[0]?.size > maxSizeMB * 1024 * 1024) {
                        return "File size should not exceed 9 MB";
                      }
                      return true;
                    }
                  }
                })}
              />
              { errors.image && <ErrorMessage>{errors.image.message}</ErrorMessage>}
            </FormLabel>
            <FormLabel>
              For Members Only:
              <FormSelection
                defaultValue={""}
                {...register("is_for_members_only", { required: true })}
              >
                <FormOption disabled value="">
                  select an option
                </FormOption>
                <FormOption value={true}>Yes</FormOption>
                <FormOption value={false}>No</FormOption>
              </FormSelection>
            </FormLabel>


            <FormLabel>
              Is Exco:
              <FormSelection
                defaultValue={""}
                {...register("is_exco", { required: true })}
              >
                <FormOption disabled value="">
                  select an option
                </FormOption>
                <FormOption value={true}>Yes</FormOption>
                <FormOption value={false}>No</FormOption>
              </FormSelection>
            </FormLabel>

            {watch("is_exco") === "true" && (
              <FormLabel>
                Excos Id:
                <FormSelection
                  defaultValue={""}
                  {...register("exco_id", { required: true })}
                >
                  <FormOption disabled value="">
                    select an option
                  </FormOption>
                  {excoListData.map((item) => (
                    <FormOption key={item.id} value={item.id}>
                      {item.id} || {item.name}
                    </FormOption>
                  ))}
                </FormSelection>
              </FormLabel>
            )}

            <FormLabel>
              Is Committe:
              <FormSelection
                defaultValue={""}
                {...register("is_committe", { required: true })}
              >
                <FormOption disabled value="">
                  select an option
                </FormOption>
                <FormOption value={true}>Yes</FormOption>
                <FormOption value={false}>No</FormOption>
              </FormSelection>
            </FormLabel>

            {watch("is_committe") === "true" && (
              <FormLabel>
                Committe Name:
                <FormSelection
                  defaultValue={""}
                  {...register("commitee_name", { required: true })}
                >
                  {committeeData.map((item) => (
                    <FormOption key={item.id} value={item.id}>
                      {item.id} || {item.name}
                    </FormOption>
                  ))}
                </FormSelection>
              </FormLabel>
            )}

            <FormLabel>
              Is Member:
              <FormSelection
                defaultValue={""}
                {...register("is_member", { required: true })}
              >
                <FormOption disabled value="">
                  select an option
                </FormOption>
                <FormOption value={true}>Yes</FormOption>
                <FormOption value={false}>No</FormOption>
              </FormSelection>
            </FormLabel>

            <TextEditor
              value={content}
              onChange={setContent}
              placeholder="Write your content here..."
            />

            <SubConBtnHold>
              <SubConBtn
                type={"submit"}
                value="Add"
                disabled={createLoading}
                typex="filled"
              />
              <SubConBtn
                type={"button"}
                value="Cancel"
                disabled={createLoading}
                onClick={close}
              />
            </SubConBtnHold>
          </Form>
        </SubCon>
      ) : (
        <small style={{ color: "white", fontSize: "20px" }}>
          An error occured while fetching data. Kindly refresh page.
        </small>
      )}
    </BackDrop>
  );
};

export default AddNews;
